import React, { useState } from 'react';
import Button from '../common/Button';

const DataTable = ({ columns, data, actions, onRowClick, pageSize = 10 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  // Sort data
  let sortedData = [...data];
  if (sortConfig.key) {
    sortedData.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const tableStyle = {
    width: '100%',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    overflow: 'hidden'
  };

  const tableInnerStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = {
    padding: '16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: 'var(--surface-light)',
    borderBottom: '1px solid var(--border)',
    cursor: 'pointer',
    userSelect: 'none'
  };

  const tdStyle = {
    padding: '16px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border)'
  };

  const rowStyle = (index) => ({
    transition: 'background 0.2s ease',
    cursor: onRowClick ? 'pointer' : 'default'
  });

  const paginationStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderTop: '1px solid var(--border)',
    background: 'var(--surface-light)'
  };

  const paginationInfoStyle = {
    fontSize: '14px',
    color: 'var(--text-secondary)'
  };

  const paginationButtonsStyle = {
    display: 'flex',
    gap: '8px'
  };

  const pageButtonStyle = (isActive) => ({
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    background: isActive ? 'var(--primary)' : 'var(--surface)',
    color: isActive ? 'white' : 'var(--text-primary)',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  });

  const actionsStyle = {
    display: 'inline-flex',
    gap: '8px',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap'
  };

  const actionHeaderStyle = {
    ...thStyle,
    width: '1%',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    cursor: 'default'
  };

  const actionCellStyle = {
    ...tdStyle,
    width: '1%',
    whiteSpace: 'nowrap',
    textAlign: 'right'
  };

  return (
    <div style={tableStyle}>
      <table style={tableInnerStyle}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={thStyle}
                onClick={() => col.sortable !== false && handleSort(col.key)}
                onMouseEnter={(e) => {
                  if (col.sortable !== false) {
                    e.target.style.background = 'var(--surface)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.background = 'var(--surface-light)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {col.label}
                  {sortConfig.key === col.key && (
                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
            ))}
            {actions && <th style={actionHeaderStyle}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((row, index) => (
            <tr
              key={index}
              style={rowStyle(index)}
              onClick={() => onRowClick && onRowClick(row)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--surface-light)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {columns.map((col) => (
                <td key={col.key} style={tdStyle}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
              {actions && (
                <td style={actionCellStyle}>
                  <div style={actionsStyle} onClick={(e) => e.stopPropagation()}>
                    {actions
                      .filter((action) => (typeof action.condition === 'function' ? action.condition(row) : true))
                      .map((action, idx) => (
                        <Button
                          key={idx}
                          variant="ghost"
                          size="small"
                          onClick={() => action.onClick(row)}
                        >
                          {action.label}
                        </Button>
                      ))}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={paginationStyle}>
          <div style={paginationInfoStyle}>
            Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length} entries
          </div>
          <div style={paginationButtonsStyle}>
            <button
              style={pageButtonStyle(false)}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.target.style.background = 'var(--surface-light)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.target.style.background = 'var(--surface)';
                }
              }}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              // Show first, last, current, and nearby pages
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    style={pageButtonStyle(pageNum === currentPage)}
                    onClick={() => setCurrentPage(pageNum)}
                    onMouseEnter={(e) => {
                      if (pageNum !== currentPage) {
                        e.target.style.background = 'var(--surface-light)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (pageNum !== currentPage) {
                        e.target.style.background = 'var(--surface)';
                      }
                    }}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <span key={pageNum} style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>;
              }
              return null;
            })}
            <button
              style={pageButtonStyle(false)}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.target.style.background = 'var(--surface-light)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.target.style.background = 'var(--surface)';
                }
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;